/* ==========================================================================
   whero-meganav.js — header behavior: desktop mega-menu, mobile overlay,
   sticky scroll shadow, cart-count sync.
   --------------------------------------------------------------------------
   Every behavior here is additive — the header renders and is usable with
   JS disabled:
     - Mega-menu triggers are <button>s; without JS they're inert
       (aria-expanded="false" forever) but all top-level links still work.
     - Mobile hamburger is a <button>; without JS it's inert but the full
       nav list is still present in the DOM, visible if CSS loaded.

   Accessibility contract (per the acceptance criteria):
     - aria-expanded toggles on every trigger
     - Escape closes the open mega/overlay and returns focus to the trigger
     - ArrowLeft/Right moves between top-level triggers
     - ArrowDown on a mega trigger opens it and focuses the first menuitem
     - ArrowUp/Down inside an open mega cycles through menuitems
     - Mouse hover opens with a 120ms intent delay (kills flicker)
     - Only one mega open at a time; clicking outside closes all
   ========================================================================== */
(function () {
  'use strict';

  var HOVER_OPEN_MS = 120;
  var HOVER_CLOSE_MS = 120;
  var SCROLL_SHADOW_THRESHOLD = 8;

  // Recognized mega-nav trigger values. Wiring is attribute-driven
  // (querySelectorAll('[data-whero-trigger]')) so this list is
  // documentation only — every entry needs a matching
  // snippets/whero-header-mega-<value>.liquid and aria-controls target.
  var KNOWN_TRIGGERS = ['flavors', 'locations', 'shop', 'events'];

  /* ---- mega-nav state -------------------------------------------------- */

  var openMega = null;  // the open .whero-mega element
  var openTrigger = null;
  var hoverOpenTimer = null;
  var hoverCloseTimer = null;

  function setMegaOpen(triggerEl, megaEl, isOpen) {
    if (!triggerEl || !megaEl) return;
    if (isOpen) {
      closeAllMegas(triggerEl);
      triggerEl.setAttribute('aria-expanded', 'true');
      megaEl.removeAttribute('hidden');
      // requestAnimationFrame ensures the 'hidden'-removal paint happens
      // before we add the animation class so the transition fires.
      requestAnimationFrame(function () {
        megaEl.classList.add('is-open');
      });
      openMega = megaEl;
      openTrigger = triggerEl;
    } else {
      triggerEl.setAttribute('aria-expanded', 'false');
      megaEl.classList.remove('is-open');
      // Wait for transition before re-hiding so screen readers
      // don't announce the disappearance mid-flight.
      setTimeout(function () {
        if (!megaEl.classList.contains('is-open')) {
          megaEl.setAttribute('hidden', '');
        }
      }, 200);
      if (openMega === megaEl) { openMega = null; openTrigger = null; }
    }
  }

  function closeAllMegas(except) {
    var triggers = document.querySelectorAll('[data-whero-trigger]');
    for (var i = 0; i < triggers.length; i++) {
      if (triggers[i] === except) continue;
      var t = triggers[i];
      var megaId = t.getAttribute('aria-controls');
      var mega = megaId ? document.getElementById(megaId) : null;
      if (t.getAttribute('aria-expanded') === 'true') {
        setMegaOpen(t, mega, false);
      }
    }
  }

  function menuitemsIn(megaEl) {
    return megaEl.querySelectorAll('[role="menuitem"]');
  }

  function wireMega(triggerEl) {
    var megaId = triggerEl.getAttribute('aria-controls');
    var megaEl = megaId ? document.getElementById(megaId) : null;
    if (!megaEl) return;
    var item = triggerEl.closest('.whero-header__item');

    triggerEl.addEventListener('click', function (e) {
      e.preventDefault();
      var isOpen = triggerEl.getAttribute('aria-expanded') === 'true';
      setMegaOpen(triggerEl, megaEl, !isOpen);
    });

    triggerEl.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMegaOpen(triggerEl, megaEl, true);
        var first = menuitemsIn(megaEl)[0];
        if (first) first.focus();
      } else if (e.key === 'Escape') {
        setMegaOpen(triggerEl, megaEl, false);
      }
    });

    // Hover-intent (desktop pointer only — coarse pointers don't trigger).
    if (item) {
      item.addEventListener('mouseenter', function () {
        if (window.matchMedia('(hover: none)').matches) return;
        clearTimeout(hoverCloseTimer);
        clearTimeout(hoverOpenTimer);
        hoverOpenTimer = setTimeout(function () {
          setMegaOpen(triggerEl, megaEl, true);
        }, HOVER_OPEN_MS);
      });
      item.addEventListener('mouseleave', function () {
        if (window.matchMedia('(hover: none)').matches) return;
        clearTimeout(hoverOpenTimer);
        clearTimeout(hoverCloseTimer);
        hoverCloseTimer = setTimeout(function () {
          setMegaOpen(triggerEl, megaEl, false);
        }, HOVER_CLOSE_MS);
      });
    }

    // Keyboard navigation inside the mega.
    megaEl.addEventListener('keydown', function (e) {
      var items = Array.prototype.slice.call(menuitemsIn(megaEl));
      var idx = items.indexOf(document.activeElement);
      if (e.key === 'Escape') {
        e.preventDefault();
        setMegaOpen(triggerEl, megaEl, false);
        triggerEl.focus();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        var nextIdx = idx < items.length - 1 ? idx + 1 : 0;
        if (items[nextIdx]) items[nextIdx].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        var prevIdx = idx > 0 ? idx - 1 : items.length - 1;
        if (items[prevIdx]) items[prevIdx].focus();
      } else if (e.key === 'Tab') {
        // Let natural tab leave the mega; close it after a tick.
        setTimeout(function () {
          if (!megaEl.contains(document.activeElement)) {
            setMegaOpen(triggerEl, megaEl, false);
          }
        }, 0);
      }
    });
  }

  function wireTopLevelArrowKeys() {
    // Arrow Left/Right between top-level triggers (including non-mega links).
    var container = document.querySelector('.whero-header__primary');
    if (!container) return;
    container.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var tops = Array.prototype.slice.call(
        container.querySelectorAll(':scope > li > .whero-header__trigger, :scope > li > .whero-header__link')
      );
      var idx = tops.indexOf(document.activeElement);
      if (idx === -1) return;
      e.preventDefault();
      var next = e.key === 'ArrowRight'
        ? (idx < tops.length - 1 ? idx + 1 : 0)
        : (idx > 0 ? idx - 1 : tops.length - 1);
      if (tops[next]) tops[next].focus();
    });
  }

  function wireOutsideClick() {
    document.addEventListener('click', function (e) {
      if (!openMega) return;
      if (openMega.contains(e.target)) return;
      if (openTrigger && openTrigger.contains(e.target)) return;
      setMegaOpen(openTrigger, openMega, false);
    });
  }

  /* ---- mobile overlay -------------------------------------------------- */

  var scrollY = 0;
  var lastFocusBeforeOverlay = null;

  function lockBodyScroll() {
    scrollY = window.scrollY;
    document.body.style.top = '-' + scrollY + 'px';
    document.documentElement.classList.add('whero-nav-open');
    document.body.classList.add('whero-nav-open');
    // position:fixed on body after setting top ensures restore is clean.
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
  }

  function unlockBodyScroll() {
    document.documentElement.classList.remove('whero-nav-open');
    document.body.classList.remove('whero-nav-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  function openMobileNav() {
    var overlay = document.querySelector('[data-whero-mobile-nav]');
    var hamburger = document.querySelector('[data-whero-hamburger]');
    if (!overlay) return;
    lastFocusBeforeOverlay = document.activeElement;
    overlay.removeAttribute('hidden');
    overlay.setAttribute('aria-hidden', 'false');
    // rAF lets the browser paint [hidden]-removed before we transition.
    requestAnimationFrame(function () {
      overlay.classList.add('is-open');
    });
    if (hamburger) hamburger.setAttribute('aria-expanded', 'true');
    lockBodyScroll();
    // Move focus into the overlay (first interactive element).
    var first = overlay.querySelector('.whero-mobile-nav__close, a, button');
    if (first) first.focus();
  }

  function closeMobileNav() {
    var overlay = document.querySelector('[data-whero-mobile-nav]');
    var hamburger = document.querySelector('[data-whero-hamburger]');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    setTimeout(function () {
      if (!overlay.classList.contains('is-open')) overlay.setAttribute('hidden', '');
    }, 260);
    if (hamburger) hamburger.setAttribute('aria-expanded', 'false');
    unlockBodyScroll();
    if (lastFocusBeforeOverlay && typeof lastFocusBeforeOverlay.focus === 'function') {
      lastFocusBeforeOverlay.focus();
    }
  }

  // The drawer is authored inside the whero-header section, which lives
  // inside Shopify's .shopify-section-group-header-group wrapper. That
  // wrapper (and sibling header-group sections like the status strip) can
  // create a stacking context that traps our position:fixed overlay —
  // reparenting to <body> guarantees it renders above everything.
  function reparentMobileNavToBody() {
    var overlay = document.querySelector('[data-whero-mobile-nav]');
    if (overlay && overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
  }

  function wireMobileNav() {
    reparentMobileNavToBody();
    var hamburger = document.querySelector('[data-whero-hamburger]');
    if (hamburger) hamburger.addEventListener('click', openMobileNav);

    var dismisses = document.querySelectorAll('[data-whero-mobile-dismiss]');
    for (var i = 0; i < dismisses.length; i++) {
      dismisses[i].addEventListener('click', closeMobileNav);
    }

    // Accordion submenus.
    var accordions = document.querySelectorAll('[data-whero-mobile-accordion]');
    for (var j = 0; j < accordions.length; j++) {
      var btn = accordions[j];
      btn.addEventListener('click', function (e) {
        var open = e.currentTarget.getAttribute('aria-expanded') === 'true';
        var sub = e.currentTarget.nextElementSibling;
        e.currentTarget.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (sub) {
          if (open) sub.setAttribute('hidden', '');
          else sub.removeAttribute('hidden');
        }
      });
    }

    // Escape closes the overlay when open.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var overlay = document.querySelector('[data-whero-mobile-nav]');
      if (overlay && overlay.classList.contains('is-open')) closeMobileNav();
    });
  }

  /* ---- sticky shadow --------------------------------------------------- */

  function wireStickyShadow() {
    var header = document.querySelector('[data-whero-header]');
    if (!header) return;
    function onScroll() {
      if (window.scrollY > SCROLL_SHADOW_THRESHOLD) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---- cart count ------------------------------------------------------ */

  function setCartCount(n) {
    var badges = document.querySelectorAll('[data-whero-cart-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = n;
      if (n > 0) badges[i].removeAttribute('hidden');
      else badges[i].setAttribute('hidden', '');
    }
    var mobiles = document.querySelectorAll('[data-whero-cart-count-mobile]');
    for (var j = 0; j < mobiles.length; j++) mobiles[j].textContent = n;
  }

  function refreshCart() {
    if (typeof fetch !== 'function') return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c) return;
        setCartCount(typeof c.item_count === 'number' ? c.item_count : 0);
      })
      .catch(function () { /* silent — non-critical */ });
  }

  function wireCart() {
    refreshCart();
    document.addEventListener('cart:updated', refreshCart);
    document.addEventListener('cart:refresh', refreshCart);
    // Dawn's native cart uses its own custom element events; we still
    // refresh on any POST to /cart/add by wrapping fetch. Non-invasive.
    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function (input, init) {
        var result = origFetch.apply(this, arguments);
        try {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          if (/\/cart\/(add|change|update|clear)/i.test(url)) {
            result.then(function () { setTimeout(refreshCart, 50); });
          }
        } catch (_) {}
        return result;
      };
    }
  }

  /* ---- init ------------------------------------------------------------ */

  function init() {
    var triggers = document.querySelectorAll('[data-whero-trigger]');
    for (var i = 0; i < triggers.length; i++) wireMega(triggers[i]);
    wireTopLevelArrowKeys();
    wireOutsideClick();
    wireMobileNav();
    wireStickyShadow();
    wireCart();
  }

  window.WheroHeader = { refreshCart: refreshCart };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
